// OSS 分片上传（TG 同款思路：切片 + 并发 + 逐片重试 + 停顿检测 + 真实进度）
// 依赖后端 Multipart 接口：api.initMultipart / getMultipartPartUrl / completeMultipart / abortMultipart
// 路由由 api.uploadFile/uploadImage 按 file.size>5MB 决定，调用方无感知。
import { api } from './api'

const PART_SIZE = 5 * 1024 * 1024   // 5MB（OSS 建议分片 ≥5MB，末片可小）
const CONCURRENCY = 3               // 并发上传的分片数
const MAX_PART_RETRY = 3            // 单片最大重试次数
const STALL_MS = 30000              // 单片 30s 无进度即视为卡住 → abort 重传
const PART_TIMEOUT = 5 * 60 * 1000  // 单片硬上限 5 分钟

// 等待网络恢复：在线立即返回；离线则挂起到 window 'online' 事件（TG 式断网暂停/恢复）
function waitForOnline() {
  if (typeof navigator === 'undefined' || navigator.onLine) return Promise.resolve()
  return new Promise(resolve => {
    const on = () => { window.removeEventListener('online', on); resolve() }
    window.addEventListener('online', on)
  })
}

// 全局并发闸：限制"所有分片上传(跨多个文件)"同时在传的分片总数。
// 不限的话同时发 N 个大文件 = 3×N 个 PUT，挤爆浏览器 ~6 连接上限，排队的片收不到 progress
// 会被 30s 停顿检测误判 abort。封顶后分片拿到名额才 send()，停顿计时才开始，不误判。
const GLOBAL_MAX_INFLIGHT = 4
let _inflight = 0
const _waiters = []
function acquireSlot() {
  if (_inflight < GLOBAL_MAX_INFLIGHT) { _inflight += 1; return Promise.resolve() }
  return new Promise(resolve => _waiters.push(resolve))
}
function releaseSlot() {
  const next = _waiters.shift()
  if (next) next()            // 名额转交给排队者，_inflight 不变
  else _inflight = Math.max(0, _inflight - 1)
}

// 上传单个分片（XHR PUT），带停顿检测。成功 resolve(ETag)，失败 reject(Error)。
// onChunkProgress(loadedBytes)：本片已传字节（用于汇总总进度）
function putPart(uploadUrl, method, blob, onChunkProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let settled = false
    let stallTimer = null
    const finish = (fn, arg) => {
      if (settled) return
      settled = true
      clearTimeout(stallTimer)
      fn(arg)
    }
    const armStall = () => {
      clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        try { xhr.abort() } catch (_) {}
        finish(reject, new Error('分片上传停顿超时'))
      }, STALL_MS)
    }
    xhr.open(method || 'PUT', uploadUrl)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        if (onChunkProgress) onChunkProgress(e.loaded)
        armStall()
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // ETag 需 OSS CORS ExposeHeader 暴露，否则跨域读不到 → null
        const etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('Etag') || ''
        finish(resolve, etag)
      } else {
        finish(reject, new Error('分片上传失败: ' + xhr.status))
      }
    }
    xhr.onerror = () => finish(reject, new Error('网络错误'))
    xhr.ontimeout = () => finish(reject, new Error('分片上传超时'))
    xhr.timeout = PART_TIMEOUT
    armStall()          // send 后 onprogress 可能迟迟不来，先 arm 一次
    xhr.send(blob)
  })
}

export async function multipartUpload(file, onProgress) {
  const contentType = file.type || 'application/octet-stream'

  // 1. 初始化
  const initResp = await api.initMultipart({ fileName: file.name, contentType, fileSize: file.size })
  const initData = (initResp && initResp.data) ? initResp.data : initResp
  const uploadId = initData && initData.uploadId
  const objectKey = initData && initData.objectKey
  if (!uploadId || !objectKey) throw new Error('初始化分片上传失败')

  // 2. 切片 + 进度账本
  const totalParts = Math.ceil(file.size / PART_SIZE)
  const parts = new Array(totalParts)            // { partNumber, etag }
  const partLoaded = new Array(totalParts).fill(0)  // 各片已传字节
  let aborted = false   // 某片彻底失败时置位 → 其它 worker 尽快停（消除未处理拒绝 + 停掉无谓 PUT）
  let lastPct = -1
  const emitProgress = () => {
    if (!onProgress) return
    const loaded = partLoaded.reduce((a, b) => a + b, 0)
    // 合并前最高到 99%，complete 成功才 100%
    const pct = Math.min(99, Math.round(loaded / file.size * 100))
    if (pct !== lastPct) { lastPct = pct; onProgress(pct) }
  }

  // 单片任务（含 MAX_PART_RETRY 次重试 + 退避）
  const doPart = async (idx) => {
    const partNumber = idx + 1
    const start = idx * PART_SIZE
    const blob = file.slice(start, Math.min(start + PART_SIZE, file.size))
    let onlineErrors = 0   // 只统计"在线时的真实错误"，离线挂起不计数
    while (true) {
      if (aborted) return             // 整单已判失败，本片不再尝试
      await waitForOnline()           // 离线 → 挂起等网络恢复（TG 式暂停；来网再续传该片）
      await acquireSlot()             // 拿到全局名额才真正开传（拿不到就排队，不占用 30s 停顿计时）
      try {
        const urlResp = await api.getMultipartPartUrl({ objectKey, uploadId, partNumber })
        const urlData = (urlResp && urlResp.data) ? urlResp.data : urlResp
        if (!urlData || !urlData.uploadUrl) throw new Error('获取分片地址失败')
        const etag = await putPart(urlData.uploadUrl, urlData.method, blob, (loaded) => {
          partLoaded[idx] = loaded
          emitProgress()
        })
        if (!etag) throw new Error('未取到分片 ETag（检查 OSS CORS ExposeHeader: ETag）')
        partLoaded[idx] = blob.size
        emitProgress()
        parts[idx] = { partNumber, etag }
        releaseSlot()
        return
      } catch (e) {
        releaseSlot()         // 失败先放名额（退避期间不占用），让别的片/文件能传
        partLoaded[idx] = 0   // 该片重来，进度回退（已传完的片不受影响 → 续传只补未完成片）
        emitProgress()
        // 断网导致的失败 → 不计预算，回顶部 waitForOnline 挂起，网络恢复后续传该片
        if (typeof navigator !== 'undefined' && !navigator.onLine) continue
        // 在线下的真实错误 → 有限重试 + 退避，超限才整单失败
        onlineErrors += 1
        if (onlineErrors >= MAX_PART_RETRY) throw e
        await new Promise(r => setTimeout(r, 800 * onlineErrors))
        continue
      }
    }
  }

  // 3. 并发池：CONCURRENCY 个 worker 抢占式取片号
  try {
    let next = 0
    let fatalErr = null
    const worker = async () => {
      while (next < totalParts && !aborted) {
        const idx = next
        next += 1
        try {
          await doPart(idx)
        } catch (e) {
          fatalErr = fatalErr || e
          aborted = true   // 通知其它 worker 停；worker 内部已兜错 → Promise.all 不会 reject（无未处理拒绝）
          return
        }
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, totalParts) }, () => worker())
    )
    if (fatalErr) throw fatalErr   // 统一在此抛，走下面 catch 的 abort 清理

    // 4. 合并（回传 fileName/fileSize/contentType 供后端回显）
    const completeResp = await api.completeMultipart({
      objectKey, uploadId, parts,
      fileName: file.name, fileSize: file.size, contentType
    })
    const cData = (completeResp && completeResp.data) ? completeResp.data : completeResp
    if (onProgress) onProgress(100)
    return {
      url: (cData && cData.accessUrl) || '',
      objectKey: (cData && cData.objectKey) || objectKey,
      fileName: (cData && cData.fileName) || file.name,
      fileSize: (cData && cData.fileSize) || file.size,
      mimeType: (cData && cData.mimeType) || contentType
    }
  } catch (e) {
    // 失败/取消清理碎片（fire-and-forget，不阻塞错误抛出）
    try { api.abortMultipart({ objectKey, uploadId }) } catch (_) {}
    throw e
  }
}
